import { LoggedInUser, User } from 'entities/user'

export interface Application {
    id: string
    userId: string
    jobId: string
    timestamp: string
    state: ApplicationState
    score: number
    conversationId?: string
}

export enum ApplicationState {
    Pending = 'pending',
    Rejected = 'rejected',
    Passed = 'pass',
    Hired = 'hired',
    Approved = 'approved',
    Interviewing = 'get_in_touch',
}

export const stateToTextAdmin: Record<ApplicationState, string> = {
    [ApplicationState.Pending]: 'Pending',
    [ApplicationState.Rejected]: 'Rejected',
    [ApplicationState.Approved]: 'Approved',
    [ApplicationState.Interviewing]: 'Interviewing',
    [ApplicationState.Hired]: 'Hired',
    [ApplicationState.Passed]: 'Passed',
}

export const stateToTextCompany: Record<ApplicationState, string> = {
    [ApplicationState.Pending]: 'Pending',
    [ApplicationState.Rejected]: 'Rejected',
    /* approved state is called pending for companies
       because "approved" means approved by admins
       but applications in this state were not approved by companies yet
       that's why they are "pending"
    */
    [ApplicationState.Approved]: 'Pending',
    [ApplicationState.Interviewing]: 'Interviewing',
    [ApplicationState.Hired]: 'Hired',
    [ApplicationState.Passed]: 'Passed',
}

export const stateToText = (
    me: Pick<LoggedInUser, 'isAdmin'>,
    state: ApplicationState,
) => {
    if (me.isAdmin) {
        return stateToTextAdmin[state]
    }
    return stateToTextCompany[state]
}

export const stateToNextStatesAdmin: Record<ApplicationState, ApplicationState[]> = {
    [ApplicationState.Pending]: [ApplicationState.Approved, ApplicationState.Rejected],
    [ApplicationState.Rejected]: [ApplicationState.Approved],
    [ApplicationState.Approved]: [ApplicationState.Interviewing, ApplicationState.Passed],
    [ApplicationState.Interviewing]: [ApplicationState.Hired, ApplicationState.Passed],
    [ApplicationState.Hired]: [ApplicationState.Interviewing],
    [ApplicationState.Passed]: [ApplicationState.Interviewing],
}

export const stateToNextStatesCompany: Record<ApplicationState, ApplicationState[]> = {
    [ApplicationState.Pending]: [ApplicationState.Interviewing, ApplicationState.Passed],
    [ApplicationState.Rejected]: [ApplicationState.Interviewing],
    [ApplicationState.Approved]: [ApplicationState.Interviewing, ApplicationState.Passed],
    [ApplicationState.Interviewing]: [ApplicationState.Hired, ApplicationState.Passed],
    [ApplicationState.Hired]: [ApplicationState.Interviewing],
    [ApplicationState.Passed]: [ApplicationState.Interviewing],
}

export const stateToNextStates = (state: ApplicationState, user: Pick<User, 'type'>) => {
    if (user.type === 'company') {
        return stateToNextStatesCompany[state]
    }
    return stateToNextStatesAdmin[state]
}

////////////////////////////////////////////////////////////////////////////////

// we need to map next application state to some action
// don't ask me why can't we just pass next state, idk
export enum ApplicationAction {
    Pass = 'pass',
    Hire = 'hire',
    Interview = 'get_in_touch',
    Approve = 'approve',
    Reject = 'reject',
}

const actionToText_: Record<ApplicationAction, string> = {
    [ApplicationAction.Approve]: 'Approve',
    [ApplicationAction.Interview]: 'Get in touch',
    [ApplicationAction.Hire]: 'Hire',
    [ApplicationAction.Pass]: 'Pass',
    [ApplicationAction.Reject]: 'Reject',
}

export function actionToText(currentState: ApplicationState, action: ApplicationAction) {
    if (
        [
            ApplicationState.Passed,
            ApplicationState.Rejected,
            ApplicationState.Hired,
        ].includes(currentState) &&
        action === ApplicationAction.Interview
    ) {
        return 'Back To Interviewing'
    }
    return actionToText_[action]
}

type IconColor = 'green' | 'red'

const actionToIconColor_: Record<ApplicationAction, IconColor> = {
    [ApplicationAction.Approve]: 'green',
    [ApplicationAction.Interview]: 'green',
    [ApplicationAction.Hire]: 'green',
    [ApplicationAction.Pass]: 'red',
    [ApplicationAction.Reject]: 'red',
}

export function iconColor(
    currentState: ApplicationState,
    action: ApplicationAction,
): IconColor | undefined {
    return actionToIconColor_[action]
}

export const stateToAction = (state: ApplicationState) => {
    if (state === ApplicationState.Pending) {
        throw new Error("Unreachable code, pending state shouldn't be mapped to action")
    }

    const mapper: Record<ApplicationState, ApplicationAction> = {
        [ApplicationState.Rejected]: ApplicationAction.Reject,
        [ApplicationState.Passed]: ApplicationAction.Pass,
        [ApplicationState.Hired]: ApplicationAction.Hire,
        [ApplicationState.Interviewing]: ApplicationAction.Interview,
        [ApplicationState.Approved]: ApplicationAction.Approve,
        // unreachable state
        [ApplicationState.Pending]: ApplicationAction.Reject,
    }

    return mapper[state]
}

////////////////////////////////////////////////////////////////////////////////

export type StateFrequency = { state: ApplicationState; count: number }

export type JobApplicationStateFrequencies = {
    jobId: string
    stateFrequencies: StateFrequency[]
}

export const ApplicationStateToColorScheme: Record<ApplicationState, string> = {
    [ApplicationState.Pending]: 'yellow_wh',
    [ApplicationState.Interviewing]: 'secondary_wh',
    [ApplicationState.Rejected]: 'red_wh',
    [ApplicationState.Hired]: 'green_wh',
    [ApplicationState.Approved]: 'yellow_wh',
    [ApplicationState.Passed]: 'red_wh',
} as const

export const ApplicationStateToCandidateDescription: Record<ApplicationState, string> = {
    [ApplicationState.Pending]: 'Waiting for review',
    [ApplicationState.Interviewing]: 'Interviewing',
    [ApplicationState.Rejected]: 'Rejected by WorksHub',
    [ApplicationState.Hired]: 'Hired',
    [ApplicationState.Approved]: 'Waiting for approval',
    [ApplicationState.Passed]: 'Rejected by company',
} as const
